import { Loader } from '@/components';
import { useNavigation } from '@/hooks/useNavigation';
import { PAGE } from '@/modules/page/routes';
import { OpinionPollsProvider } from '@/modules/polls/components/PollsOpinion/OpinionPollsProvider';
import { TABS_MAP } from '@/modules/polls/pollsStore';
import { OpinionsProvider } from '@/modules/profile/OpinionsProvider';
import { useStore } from '@/store';

import { observer } from 'mobx-react-lite';
import { Suspense, lazy, useEffect } from 'react';
import { AliveScope, KeepAlive } from 'react-activation';
import {
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
  useParams,
} from 'react-router-dom';

import ProtectedRoute from './ProtectedRoute';

const Wallet = lazy(() =>
  import('@/modules/wallet').then((module) => ({ default: module.Wallet })),
);
const Profile = lazy(() =>
  import('@/modules/profile').then((module) => ({ default: module.Profile })),
);
const SelectAsset = lazy(() =>
  import('@/modules/wallet/components/SelectAsset').then((module) => ({
    default: module.SelectAsset,
  })),
);
const TransactionFinish = lazy(() =>
  import('@/modules/wallet/components/TransactionFinish').then((module) => ({
    default: module.TransactionFinish,
  })),
);
const SelectDepositMethod = lazy(() =>
  import('@/modules/wallet/components/SelectDepositMethod').then((module) => ({
    default: module.SelectDepositMethod,
  })),
);
const DepositForm = lazy(() =>
  import('@/modules/wallet/components/DepositForm').then((module) => ({
    default: module.DepositForm,
  })),
);
const ExternalDepositForm = lazy(() =>
  import('@/modules/wallet/components/ExternalDepositForm').then((module) => ({
    default: module.ExternalDepositForm,
  })),
);
const WithdrawForm = lazy(() =>
  import('@/modules/wallet/components/WithdrawForm').then((module) => ({
    default: module.WithdrawForm,
  })),
);
const TransactionsHistory = lazy(() =>
  import('@/modules/wallet/components/TransactionsHistory').then((module) => ({
    default: module.TransactionsHistory,
  })),
);
const Polls = lazy(() =>
  import('@/modules/polls').then((module) => ({ default: module.Polls })),
);
const PollPage = lazy(() =>
  import('@/modules/polls/components/PollPage').then((module) => ({
    default: module.PollPage,
  })),
);
const OpinionsPage = lazy(() =>
  import('@/modules/opinions/OpinionsPage').then((module) => ({
    default: module.OpinionsPage,
  })),
);
const ProfileOpinionPage = lazy(() =>
  import('@/modules/profile/components/ProfileOpinionPage').then((module) => ({
    default: module.ProfileOpinionPage,
  })),
);
const OpinionPage = lazy(() =>
  import('@/modules/opinions/components/OpinionPage').then((module) => ({
    default: module.OpinionPage,
  })),
);
const TournamentPage = lazy(() =>
  import('@/modules/tournaments/components/TournamentPage').then((module) => ({
    default: module.TournamentPage,
  })),
);
const TournamentOpinionPage = lazy(() =>
  import('@/modules/tournaments/components/TournamentOpinionPage').then(
    (module) => ({
      default: module.TournamentOpinionPage,
    }),
  ),
);
const Tasks = lazy(() =>
  import('@/modules/tasks/Tasks').then((module) => ({ default: module.Tasks })),
);
const Leaderboard = lazy(() =>
  import('@/modules/leaderboard/Leaderboard').then((module) => ({
    default: module.Leaderboard,
  })),
);
const Ugc = lazy(() =>
  import('@/modules/ugc/Ugc').then((module) => ({
    default: module.Ugc,
  })),
);
const CreateOpinion = lazy(() =>
  import('@/modules/ugc/components/CreateOpinion').then((module) => ({
    default: module.CreateOpinion,
  })),
);
const CreatePoll = lazy(() =>
  import('@/modules/polls/components/CreatePoll').then((module) => ({
    default: module.CreatePoll,
  })),
);
const CreateTournament = lazy(() =>
  import('@/modules/tournaments/components/CreateTournament').then(
    (module) => ({
      default: module.CreateTournament,
    }),
  ),
);

const addSlash = (path: string) => `/${path}`;

export const PageContainer = observer(() => {
  const { goTo } = useNavigation();
  const { startParams } = useStore();
  const location = useLocation();
  const match = matchPath('/opinions/:id', location.pathname);
  const opinionId = match?.params?.id;

  useEffect(() => {
    if (startParams?.pollId) {
      goTo(PAGE.POLL_ID, { id: startParams.pollId });
    }
    if (startParams?.opId) {
      goTo(PAGE.OPINION_ID, { id: startParams.opId });
    }
    if (startParams?.tournamentId) {
      goTo(PAGE.TOURNAMENT_ID, { id: startParams.tournamentId });
    }
    if (startParams?.profId) {
      goTo(PAGE.PROFILE_ID, { id: startParams.profId });
    }
    if (startParams?.startPage) {
      if (startParams.startPage === 'POLLS') goTo(PAGE.POLLS);
      if (startParams.startPage === 'TOURNAMENTS') goTo(PAGE.TOURNAMENTS);
      if (startParams.startPage === 'OPINIONS') goTo(PAGE.OPINIONS);
      if (startParams.startPage === 'CREATE') goTo(PAGE.UGC);
    }
  }, [startParams]);

  return (
    <Suspense fallback={<Loader isHeaderVisible />}>
      <OpinionPollsProvider initialOpinionId={opinionId}>
        <AliveScope>
          <Routes>
            <Route path="/" element={<Navigate to="/opinions" replace />} />
            <Route
              path={addSlash(PAGE.POLLS)}
              element={<Polls activeTab={TABS_MAP.POLLS} />}
            />

            <Route path={addSlash(PAGE.OPINIONS)} element={<OpinionsPage />} />
            <Route path={addSlash(PAGE.OPINION_ID)} element={<OpinionPage />} />

            <Route
              path={addSlash(PAGE.LEADERBOARD)}
              element={<Leaderboard />}
            />
            <Route
              path={addSlash(PAGE.PROFILE_ID)}
              element={
                <KeepAlive>
                  <ProfileWithOpinionsProvider />
                </KeepAlive>
              }
            />
            <Route
              path={addSlash(PAGE.PROFILE_ID_OPINION_ID)}
              element={<ProfileOpinionPage />}
            />
            <Route path={addSlash(PAGE.POLL_ID)} element={<PollPage />} />
            <Route path={addSlash(PAGE.UGC)} element={<Ugc />} />
            <Route
              path={addSlash(PAGE.TOURNAMENTS)}
              element={<Polls activeTab={TABS_MAP.TOURNAMENTS} />}
            />
            <Route
              path={addSlash(PAGE.TOURNAMENT_ID)}
              element={
                <KeepAlive>
                  <TournamentPage />
                </KeepAlive>
              }
            />

            <Route element={<ProtectedRoute />}>
              <Route path={addSlash(PAGE.TASKS)} element={<Tasks />} />
              <Route
                path={addSlash(PAGE.TOURNAMENT_ID_OPINION_ID)}
                element={<TournamentOpinionPage />}
              />
              <Route
                path={addSlash(PAGE.CREATE_OPINION)}
                element={<CreateOpinion />}
              />
              <Route
                path={addSlash(PAGE.CREATE_POLL)}
                element={<CreatePoll />}
              />
              <Route path={addSlash(PAGE.WALLET)} element={<Wallet />} />
              <Route
                path={addSlash(PAGE.WALLET_TRANSACTIONS_HISTORY)}
                element={<TransactionsHistory />}
              />
              <Route
                path={addSlash(PAGE.WALLET_DEPOSIT_SELECT)}
                element={<SelectAsset />}
              />
              <Route
                path={addSlash(PAGE.WALLET_WITHDRAW_SELECT)}
                element={<SelectAsset />}
              />
              <Route
                path={addSlash(PAGE.WALLET_TRANSACTION_FINISH)}
                element={<TransactionFinish />}
              />
              <Route
                path={addSlash(PAGE.WALLET_DEPOSIT_METHOD_SELECT)}
                element={<SelectDepositMethod />}
              />
              <Route
                path={addSlash(PAGE.WALLET_WITHDRAW_FORM)}
                element={<WithdrawForm />}
              />
              <Route
                path={addSlash(PAGE.WALLET_EXTERNAL_DEPOSIT)}
                element={<ExternalDepositForm />}
              />
              <Route
                path={addSlash(PAGE.WALLET_DEPOSIT_FORM)}
                element={<DepositForm />}
              />
              <Route
                path={addSlash(PAGE.CREATE_TOURNAMENT)}
                element={<CreateTournament />}
              />
            </Route>
            <Route path="*" element={<Navigate to="/opinions" replace />} />
          </Routes>
        </AliveScope>
      </OpinionPollsProvider>
    </Suspense>
  );
});

const ProfileWithOpinionsProvider = () => {
  const { id: profileId } = useParams();
  const {
    accountStore: { accountData },
  } = useStore();
  const me = profileId === accountData._id;

  return (
    <OpinionsProvider profileId={profileId!} me={me}>
      <Profile />
    </OpinionsProvider>
  );
};
